/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import React, { Component, RefObject } from 'react';
import { ILinkOptions } from 'src/adhoc/store/types/StateTypes';
import { HeaderContainerComponent } from './parts/HeaderContainerComponent';
import { BodyContainerComponent } from './parts/BodyContainerComponent';
import {
    ITable,
    ITableVisualizationDynamicProperties,
    ITableVisualizationStaticProperties
} from './types/tableVisualizationTypes';
import { ILinkToRefObjectPair } from '../types/commonTypes';
import { GrantTotalContainerComponent } from './parts/GrantTotalContainerComponent';
import AdHocMessageComponent from '../../AdHocMessageComponent';
import i18n from '../../../../i18n/AdHocBundle.properties';

export interface ITableVisualizationProps {
    table: ITable,
    linkOptions: ILinkOptions,
    dynamicProperties: ITableVisualizationDynamicProperties,
    staticProperties: ITableVisualizationStaticProperties,
    onUpdateDynamicProperties: (dynamicProperties: ITableVisualizationDynamicProperties) => void,
}

export default class TableVisualization extends Component<ITableVisualizationProps> {

    linkToRefObjectPairs: ILinkToRefObjectPair[];

    headerRef?: RefObject<HTMLDivElement>;

    bodyRef?: RefObject<HTMLDivElement>;

    bottomRef?: RefObject<HTMLDivElement>;

    tableContainerRef?: RefObject<HTMLDivElement>;

    constructor(props: ITableVisualizationProps) {
        super(props);

        this.onBodyContainerScroll = this.onBodyContainerScroll.bind(this);
        this.onBottomContainerScroll = this.onBottomContainerScroll.bind(this);

        this.linkToRefObjectPairs = [];
        this.tableContainerRef = React.createRef();
    }

    componentDidMount() {
        this.callBeforeRender();
    }

    shouldComponentUpdate() {
        this.linkToRefObjectPairs = [];
        return true;
    }

    componentDidUpdate() {
        let shouldCallBeforeRender = true;

        this.verifyHorizontalScrollValidity();

        if (this.headerRef && this.headerRef.current) {
            if (this.props.dynamicProperties.headerHeight === -1) {
                shouldCallBeforeRender = false;
                this.props.onUpdateDynamicProperties({ headerHeight: this.headerRef.current.offsetHeight } as ITableVisualizationDynamicProperties);
            }
        }

        if (this.bottomRef && this.bottomRef.current) {
            if (this.props.dynamicProperties.bottomHeight === -1) {
                shouldCallBeforeRender = false;
                this.props.onUpdateDynamicProperties({ bottomHeight: this.bottomRef.current.offsetHeight } as ITableVisualizationDynamicProperties);
            }
        }

        if (this.tableContainerRef && this.tableContainerRef.current) {
            if (this.props.dynamicProperties.tableContainerHeight === -1 || this.props.dynamicProperties.tableContainerHeight !== this.tableContainerRef.current.offsetHeight) {
                shouldCallBeforeRender = false;
                this.props.onUpdateDynamicProperties({ tableContainerHeight: this.tableContainerRef.current.offsetHeight } as ITableVisualizationDynamicProperties);
            }
        }

        if (shouldCallBeforeRender) {
            this.callBeforeRender();
        }
    }

    onBodyContainerScroll() {
        if (this.bodyRef && this.bodyRef.current) {
            if (this.headerRef && this.headerRef.current) {
                this.headerRef.current.scrollLeft = this.bodyRef.current.scrollLeft;
            }
            if (this.bottomRef && this.bottomRef.current) {
                this.bottomRef.current.scrollLeft = this.bodyRef.current.scrollLeft;
            }
        }
    }

    onBottomContainerScroll() {
        if (this.bottomRef && this.bottomRef.current && this.headerRef && this.headerRef.current) {
            this.headerRef.current.scrollLeft = this.bottomRef.current.scrollLeft;
        }
    }

    callBeforeRender() {
        if (!this.props.linkOptions) {
            return;
        }

        const { beforeRender } = this.props.linkOptions;
        const linkToElemPairs = this.linkToRefObjectPairs.map((pair) => ({ data: pair.data, element: pair.ref.current }));
        // eslint-disable-next-line no-unused-expressions
        beforeRender && beforeRender(linkToElemPairs);
    }

    verifyHorizontalScrollValidity() {
        if (this.tableContainerRef && this.tableContainerRef.current && this.bodyRef && this.bodyRef.current && this.headerRef && this.headerRef.current) {
            if (this.tableContainerRef.current.clientWidth + this.bodyRef.current.scrollLeft > this.bodyRef.current.offsetWidth) {
                this.bodyRef.current.scrollLeft = this.headerRef.current.scrollLeft;
            }
        }
    }

    render() {
        const {
            table, staticProperties, dynamicProperties, onUpdateDynamicProperties, linkOptions
        } = this.props;
        const { header, rows, bottom } = table;

        const empty = !rows.length && !bottom.cells.length;

        const showTotalsContainer = staticProperties.columns.reduce((memo, column) => (memo || !!column.showSummary), !!staticProperties.showTotals);

        const tableContainerStyles = {
            position: 'relative',
            maxWidth: '100%'
        };

        const tableHeight = dynamicProperties.contentHeight + dynamicProperties.headerHeight + dynamicProperties.bottomHeight;
        if (tableHeight <= dynamicProperties.tableContainerHeight) {
            // @ts-ignore
            tableContainerStyles.height = `${tableHeight}px`;
        }

        if (header.cells.length === 0) {
            return (
                <AdHocMessageComponent
                    message={i18n['adhoc.data.is.empty.text']}
                />
            )
        }

        return (
            <div
                ref={this.tableContainerRef}
                className="jr-mDatatableContainer jr"
                // @ts-ignore
                style={tableContainerStyles}
            >
                <HeaderContainerComponent
                    getRef={(ref) => {
                        this.headerRef = ref;
                    }}
                    header={header}
                    linkOptions={linkOptions}
                    linkToRefObjectPairs={this.linkToRefObjectPairs}
                />

                {
                    empty && <AdHocMessageComponent />
                }

                <BodyContainerComponent
                    getRef={(ref) => {
                        this.bodyRef = ref;
                    }}
                    rows={rows}
                    staticProperties={staticProperties}
                    dynamicProperties={dynamicProperties}
                    onScrollCallback={this.onBodyContainerScroll}
                    onUpdateDynamicProperties={onUpdateDynamicProperties}
                    linkOptions={linkOptions}
                    linkToRefObjectPairs={this.linkToRefObjectPairs}
                />
                <GrantTotalContainerComponent
                    getRef={(ref) => {
                        this.bottomRef = ref;
                    }}
                    useScroll={rows.length === 0 && bottom.cells.length > 0}
                    bottom={bottom}
                    bottomOffset={showTotalsContainer ? dynamicProperties.bottomHeight : 0}
                    onScrollCallback={this.onBottomContainerScroll}
                    linkOptions={linkOptions}
                    linkToRefObjectPairs={this.linkToRefObjectPairs}
                />
            </div>
        );
    }
}
